import { Looker40SDK as LookerSDK } from "@looker/sdk";
import { EmbedSession } from "../components/EmbedSession";
import { ApiSettings } from "@looker/sdk-rtl";
import * as Config from "../components/Configs";
import * as Util from "../components/Util";

const settings = new ApiSettings({
  base_url: Config.LOOKER_APP_URL,
});
const session = new EmbedSession(settings);
const sdk = new LookerSDK(session);

// Looker can not handle , and needs it to be prefixed by ^...
const escapeChar = (val: string | undefined) => {
  if (val) {
    return val.replaceAll(",", "^,");
  } else {
    return val;
  }
};

const searchProduct = async (
  tradingGroup: string | undefined,
  category: string | undefined,
  productGroup: string | undefined,
  clasz: string | undefined,
  subClass: string | undefined,
  brand: string | undefined,
  supplier: string | undefined,
  min: string | undefined,
  pin: string | undefined,
  itemName: string | undefined
): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  const limit = 50;
  const filters: {
    "product_search.commercial_trading_group"?: string;
    "product_search.commercial_category"?: string;
    "product_search.commercial_department"?: string;
    "product_search.commercial_class"?: string;
    "product_search.commercial_subclass"?: string;
    "product_search.brand_name"?: string;
    "product_search.item_name"?: string;
    "product_search.min"?: string;
    "product_search.pin"?: string;
    "product_search.supplier_name"?: string;
  } = {};

  if (itemName && itemName.length > 0) {
    filters["product_search.item_name"] =
      "%" + itemName.replaceAll(" ", "%").replaceAll(",", "^,") + "%";
  }

  if (min && min.length > 0) {
    filters["product_search.min"] = min;
  }

  if (pin && pin.length > 0) {
    filters["product_search.pin"] = pin;
  }

  if (tradingGroup && tradingGroup !== Util.ALL_VALUES) {
    filters["product_search.commercial_trading_group"] =
      tradingGroup.replaceAll(",", "^,");
  }

  if (category && category !== Util.ALL_VALUES) {
    filters["product_search.commercial_category"] = category.replaceAll(
      ",",
      "^,"
    );
  }

  if (productGroup && productGroup !== Util.ALL_VALUES) {
    filters["product_search.commercial_department"] = productGroup.replaceAll(
      ",",
      "^,"
    );
  }

  if (clasz && clasz !== Util.ALL_VALUES) {
    filters["product_search.commercial_class"] = clasz.replaceAll(",", "^,");
  }

  if (subClass && subClass !== Util.ALL_VALUES) {
    filters["product_search.commercial_subclass"] = subClass.replaceAll(
      ",",
      "^,"
    );
  }

  if (brand && brand !== Util.ALL_VALUES) {
    filters["product_search.brand_name"] = brand.replaceAll(",", "^,");
  }

  if (supplier && supplier !== Util.ALL_VALUES) {
    filters["product_search.supplier_name"] = supplier.replaceAll(",", "^,");
  }

  const model = "my_product_profile";
  const explore = "product_search";
  const fields = [
    "product_search.min",
    "product_search.tuc",
    "product_search.item_name",
    "product_search.supplier_name",
    "product_search.stores_ranged",
    "product_search.commercial_trading_group",
  ];
  const sorts = [
    "product_search.stores_ranged desc",
    "product_search.item_name asc",
    "product_search.min asc",
  ];

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts
  );
  return response;
};

const getCommercialHierarchy = async (
  tradingGroup: string,
  category: string,
  productGroup: string,
  clasz: string,
  subClass: string
): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  const filters: {
    "product.commercial_trading_group"?: string;
    "product.commercial_category"?: string;
    "product.commercial_department"?: string;
    "product.commercial_class"?: string;
    "product.commercial_subclass"?: string;
  } = {};

  if (tradingGroup !== Util.ALL_VALUES) {
    filters["product.commercial_trading_group"] = tradingGroup;
  }

  if (category !== Util.ALL_VALUES) {
    filters["product.commercial_category"] = category;
  }

  if (productGroup !== Util.ALL_VALUES) {
    filters["product.commercial_department"] = productGroup;
  }

  if (clasz !== Util.ALL_VALUES) {
    filters["product.commercial_class"] = clasz;
  }

  if (subClass !== Util.ALL_VALUES) {
    filters["product.commercial_subclass"] = subClass;
  }

  const model = "my_product_profile";
  const explore = "product";
  const fields = [
    "product.commercial_trading_group",
    "product.commercial_category",
    "product.commercial_department",
    "product.commercial_class",
    "product.commercial_subclass",
  ];
  const response = await executeAPICall(model, explore, fields, filters);
  return response;
};

const getCommercialBands = async (
  tradingGroup: string,
  category: string,
  productGroup: string,
  clasz: string,
  subClass: string
): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  let filters: {
    "product.commercial_trading_group"?: string;
    "product.commercial_category"?: string;
    "product.commercial_department"?: string;
    "product.commercial_class"?: string;
    "product.commercial_subclass"?: string;
  } = {};

  if (tradingGroup !== Util.ALL_VALUES) {
    filters["product.commercial_trading_group"] = tradingGroup;
  }

  if (category !== Util.ALL_VALUES) {
    filters["product.commercial_category"] = category;
  }

  if (productGroup !== Util.ALL_VALUES) {
    filters["product.commercial_department"] = productGroup;
  }

  if (clasz !== Util.ALL_VALUES) {
    filters = { "product.commercial_class": clasz };
  }

  if (subClass !== Util.ALL_VALUES) {
    filters["product.commercial_subclass"] = subClass;
  }

  // Do not call the service if none of the hierarchy values are provided...
  if (
    tradingGroup === Util.ALL_VALUES &&
    category === Util.ALL_VALUES &&
    productGroup === Util.ALL_VALUES &&
    clasz === Util.ALL_VALUES &&
    subClass === Util.ALL_VALUES
  ) {
    return { ok: true, value: [], error: undefined };
  }

  const model = "my_product_profile";
  const explore = "product";
  const fields = ["product.brand_name"];

  const response = await executeAPICall(model, explore, fields, filters);
  return response;
};

const getSupplierName = async (
  tradingGroup: string,
  category: string,
  productGroup: string,
  clasz: string,
  subClass: string
): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  let filters: {
    "product.commercial_trading_group"?: string;
    "product.commercial_category"?: string;
    "product.commercial_department"?: string;
    "product.commercial_class"?: string;
    "product.commercial_subclass"?: string;
  } = {};

  if (tradingGroup !== Util.ALL_VALUES) {
    filters["product.commercial_trading_group"] = tradingGroup;
  }

  if (category !== Util.ALL_VALUES) {
    filters["product.commercial_category"] = category;
  }

  if (productGroup !== Util.ALL_VALUES) {
    filters["product.commercial_department"] = productGroup;
  }

  if (clasz !== Util.ALL_VALUES) {
    filters = { "product.commercial_class": clasz };
  }

  if (clasz !== Util.ALL_VALUES) {
    filters["product.commercial_class"] = clasz;
  }

  if (subClass !== Util.ALL_VALUES) {
    filters["product.commercial_subclass"] = subClass;
  }

  // Do not call the service if none of the hierarchy values are provided...
  if (
    tradingGroup === Util.ALL_VALUES &&
    category === Util.ALL_VALUES &&
    productGroup === Util.ALL_VALUES &&
    clasz === Util.ALL_VALUES &&
    subClass === Util.ALL_VALUES
  ) {
    return { ok: true, value: [], error: undefined };
  }

  const model = "my_product_profile";
  const explore = "product";
  const fields = ["supplier.supplier_name"];

  const response = await executeAPICall(model, explore, fields, filters);
  return response;
};

/**
 * Get product details from Looker...
 * @param min Mandatory product min that has to be passed to this call.
 * @param store Pass this optional value when store specific details are needed.
 */
const getProductDetails = async (
  min: string,
  store?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  let filters = {};
  if (store) {
    filters = { "product.min": min, "location.location_description": store };
  } else {
    filters = { "product.min": min };
  }
  const model = "my_product_profile";
  const explore = "product";
  const fields = [
    "supplier.supplier_site_name",
    "supplier.ebs_supplier_site_id",
    "supplier.ebs_supplier_id",
    "supplier.supplier_name",
    "supplier.rms_supplier_id",
    "supplier.supplier_terms_description",
    "supplier.supplier_discount_percentage",

    "supplier.Supplier_Email",
    "supplier.Supplier_Lead_Contact",
    "supplier.Primary_Currency",
    "supplier.ADDR_LINE_1",
    "supplier.ADDR_LINE_2",
    "supplier.ADDR_LINE_3",
    "supplier.POSTCODE",
    "supplier.CITY_NAME",

    "product.tuc",

    "hfss_consolidated.food_drink",
    "hfss_consolidated.hfss_in_scope",
    "hfss_consolidated.hfss_score",
    "hfss_consolidated.hfss_status",
    "hfss_consolidated.hfss_category_desc",
  ];

  const response = await executeAPICall(model, explore, fields, filters);
  return response;
};

const getStoreDescriptions = async (): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  // Input parameters...
  const model = "my_product_profile";
  const limit = 700; // As per business the supermarket numbers are from 001 to 699
  const filters = { "location.storeformat_name": "Supermarket" };
  const explore = "location";
  const fields = ["location.store_description"];
  const sorts = ["location.store_description ASC"];

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts
  );
  return response;
};

const getCountriesAndRegions = async (): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  // Input parameters...
  const model = "my_product_profile";
  const limit = 50;
  const filters = { "location.storeformat_name": "Supermarket" };
  const explore = "location";
  const fields = ["location.Country", "location.Region"];

  const response = await executeAPICall(model, explore, fields, filters, limit);
  return response;
};

/**
 * Get product details from Looker...
 * @param min Mandatory product min that has to be passed to this call.
 * @param store Pass this optional value when store specific details are needed.
 */
const getProductSales = async (
  min: string,
  isDay: boolean,
  store?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "item_sales";
  const limit = 14;

  let filters = {};
  let fields: string[] = [];
  let sorts: string[] = [];

  if (store) {
    if (isDay) {
      fields = [
        "unaudited_sales.takings",
        "unaudited_sales.sales_units",
        "morrisons_calendar.week_day_cnt",
        "morrisons_calendar.calendar_date_date",
      ];

      filters = {
        "unaudited_sales.MIN": min,
        "location.store_description": store,
        "item_sales.transaction_sold_date": "14 days ago for 14 days",
        "item_sales.channel": "Supermarket",
      };

      sorts = ["morrisons_calendar.calendar_date_date desc"];
    } else {
      fields = [
        "unaudited_sales.takings",
        "unaudited_sales.sales_units",
        "morrisons_calendar.week_day_cnt",
        "morrisons_calendar.business_week_start_date",
      ];

      filters = {
        "unaudited_sales.MIN": min,
        "location.store_description": store,
        "item_sales.transaction_sold_date": "5 weeks",
        "item_sales.channel": "Supermarket",
      };

      sorts = ["morrisons_calendar.business_week_start_date desc"];
    }
  } else {
    if (isDay) {
      fields = [
        "unaudited_sales.takings",
        "unaudited_sales.sales_units",
        "morrisons_calendar.week_day_cnt",
        "morrisons_calendar.calendar_date_date",
      ];

      filters = {
        "unaudited_sales.MIN": min,
        "item_sales.transaction_sold_date": "14 days ago for 14 days",
        "item_sales.channel": "Online,Supermarket",
      };

      sorts = ["morrisons_calendar.calendar_date_date desc"];
    } else {
      fields = [
        "unaudited_sales.takings",
        "unaudited_sales.sales_units",
        "morrisons_calendar.week_day_cnt",
        "morrisons_calendar.business_week_start_date",
      ];

      filters = {
        "unaudited_sales.MIN": min,
        "item_sales.transaction_sold_date": "5 weeks",
        "item_sales.channel": "Online,Supermarket",
      };

      sorts = ["morrisons_calendar.business_week_start_date desc"];
    }
  }

  const dynamic_fields =
    // eslint-disable-next-line no-template-curly-in-string
    '[{"category":"table_calculation","label":"Average Price","expression":"${unaudited_sales.takings}/${unaudited_sales.sales_units} ","value_format":null,"value_format_name":"gbp","_kind_hint":"measure","table_calculation":"average_price","_type_hint":"number"},{"category":"table_calculation","label":"Average Daily Takings","expression":"${unaudited_sales.takings}/${morrisons_calendar.week_day_cnt} ","value_format":null,"value_format_name":"gbp","_kind_hint":"measure","table_calculation":"average_daily_takings","_type_hint":"number"},{"category":"table_calculation","label":"Average Daily Units","expression":"${unaudited_sales.sales_units}/${morrisons_calendar.week_day_cnt} ","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"average_daily_units","_type_hint":"number"}]';

  const response = await executeAPICall(
    model,
    explore,

    fields,
    filters,
    limit,
    sorts,
    undefined,
    dynamic_fields
  );
  return response;
};

/**
 * Get corporate product details from Looker...
 * @param min Mandatory product min that has to be passed to this call.
 * @param country Pass this optional value when country specific details are needed.
 * @param region Pass this optional value when region specific details are needed.
 */
const getCorporateProductSales = async (
  min: string,
  isDay: boolean,
  country?: string | undefined,
  region?: string | undefined,
  channel?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "item_sales";
  const limit = 14;

  let filters = {};
  let fields: string[] = [];
  let sorts: string[] = [];

  if (isDay) {
    fields = [
      "unaudited_sales.takings",
      "unaudited_sales.sales_units",
      "morrisons_calendar.week_day_cnt",
      "morrisons_calendar.calendar_date_date",
    ];

    filters = {
      "unaudited_sales.MIN": min,
      "location.Country": escapeChar(country),
      "location.Region": escapeChar(region),
      "item_sales.transaction_sold_date": "14 days ago for 14 days",
      "item_sales.channel": channel ? channel : "Supermarket",
    };

    sorts = ["morrisons_calendar.calendar_date_date desc"];
  } else {
    fields = [
      "unaudited_sales.takings",
      "unaudited_sales.sales_units",
      "morrisons_calendar.week_day_cnt",
      "morrisons_calendar.business_week_start_date",
    ];

    filters = {
      "unaudited_sales.MIN": min,
      "location.Country": escapeChar(country),
      "location.Region": escapeChar(region),
      "item_sales.transaction_sold_date": "5 weeks",
      "item_sales.channel": channel ? channel : "Supermarket",
    };

    sorts = ["morrisons_calendar.business_week_start_date desc"];
  }

  const dynamic_fields =
    // eslint-disable-next-line no-template-curly-in-string
    '[{"category":"table_calculation","label":"Average Price","expression":"${unaudited_sales.takings}/${unaudited_sales.sales_units} ","value_format":null,"value_format_name":"gbp","_kind_hint":"measure","table_calculation":"average_price","_type_hint":"number"},{"category":"table_calculation","label":"Average Daily Takings","expression":"${unaudited_sales.takings}/${morrisons_calendar.week_day_cnt} ","value_format":null,"value_format_name":"gbp","_kind_hint":"measure","table_calculation":"average_daily_takings","_type_hint":"number"},{"category":"table_calculation","label":"Average Daily Units","expression":"${unaudited_sales.sales_units}/${morrisons_calendar.week_day_cnt} ","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"average_daily_units","_type_hint":"number"}]';

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    dynamic_fields
  );
  return response;
};

/**
 * Get product details from Looker...
 * @param min Mandatory product min that has to be passed to this call.
 * @param store Pass this optional value when store specific details are needed.
 */
const getProductSalesByChannel = async (
  min: string,
  channel: string,
  isDay: boolean,
  store?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "item_sales";
  const limit = 14;

  let filters = {};
  let fields: string[] = [];
  let sorts: string[] = [];

  if (store) {
    if (isDay) {
      fields = [
        "unaudited_sales.takings",
        "unaudited_sales.sales_units",
        "morrisons_calendar.week_day_cnt",
        "morrisons_calendar.calendar_date_date",
      ];

      filters = {
        "unaudited_sales.MIN": min,
        "location.store_description": store,
        "item_sales.transaction_sold_date": "14 days ago for 14 days",
        "item_sales.channel": channel,
      };

      sorts = ["morrisons_calendar.calendar_date_date desc"];
    } else {
      fields = [
        "unaudited_sales.takings",
        "unaudited_sales.sales_units",
        "morrisons_calendar.week_day_cnt",
        "morrisons_calendar.business_week_start_date",
      ];

      filters = {
        "unaudited_sales.MIN": min,
        "location.store_description": store,
        "item_sales.transaction_sold_date": "5 weeks",
        "item_sales.channel": channel,
      };

      sorts = ["morrisons_calendar.business_week_start_date desc"];
    }
  } else {
    if (isDay) {
      fields = [
        "unaudited_sales.takings",
        "unaudited_sales.sales_units",
        "morrisons_calendar.week_day_cnt",
        "morrisons_calendar.calendar_date_date",
      ];

      filters = {
        "unaudited_sales.MIN": min,
        "item_sales.transaction_sold_date": "14 days ago for 14 days",
        "item_sales.channel": channel,
      };

      sorts = ["morrisons_calendar.calendar_date_date desc"];
    } else {
      fields = [
        "unaudited_sales.takings",
        "unaudited_sales.sales_units",
        "morrisons_calendar.week_day_cnt",
        "morrisons_calendar.business_week_start_date",
      ];

      filters = {
        "unaudited_sales.MIN": min,
        "item_sales.transaction_sold_date": "5 weeks",
        "item_sales.channel": channel,
      };

      sorts = ["morrisons_calendar.business_week_start_date desc"];
    }
  }

  const dynamic_fields =
    // eslint-disable-next-line no-template-curly-in-string
    '[{"category":"table_calculation","label":"Average Price","expression":"${unaudited_sales.takings}/${unaudited_sales.sales_units} ","value_format":null,"value_format_name":"gbp","_kind_hint":"measure","table_calculation":"average_price","_type_hint":"number"},{"category":"table_calculation","label":"Average Daily Takings","expression":"${unaudited_sales.takings}/${morrisons_calendar.week_day_cnt} ","value_format":null,"value_format_name":"gbp","_kind_hint":"measure","table_calculation":"average_daily_takings","_type_hint":"number"},{"category":"table_calculation","label":"Average Daily Units","expression":"${unaudited_sales.sales_units}/${morrisons_calendar.week_day_cnt} ","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"average_daily_units","_type_hint":"number"}]';

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    dynamic_fields
  );
  return response;
};

/**
 * Get product details from Looker...
 * @param min Mandatory product min that has to be passed to this call.
 * @param store Pass this optional value when store specific details are needed.
 */
const getDepotOutbound = async (
  min: string,
  isDay: boolean,
  store?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "depot_outbound";
  const limit = 14;

  let filters = {};
  let fields: string[] = [];
  let sorts: string[] = [];

  if (store) {
    if (isDay) {
      fields = [
        "depot_outbound.total_demand_qty",
        "depot_outbound.total_deamnd_fulfilled_qty",
        "morrisons_calendar.calendar_date_date",
      ];

      filters = {
        "depot_outbound.min": min,
        "location.store_description": store,
        "morrisons_calendar.calendar_date_date": "14 days ago for 14 days",
      };

      sorts = ["morrisons_calendar.calendar_date_date desc"];
    } else {
      fields = [
        "depot_outbound.total_demand_qty",
        "depot_outbound.total_deamnd_fulfilled_qty",
        "morrisons_calendar.business_week_start_date",
      ];

      filters = {
        "depot_outbound.min": min,
        "location.store_description": store,
        "morrisons_calendar.calendar_date_date": "5 weeks",
      };

      sorts = ["morrisons_calendar.business_week_start_date desc"];
    }
  } else {
    if (isDay) {
      fields = [
        "depot_outbound.total_demand_qty",
        "depot_outbound.total_deamnd_fulfilled_qty",
        "morrisons_calendar.calendar_date_date",
      ];

      filters = {
        "depot_outbound.min": min,
        "morrisons_calendar.calendar_date_date": "14 days ago for 14 days",
      };

      sorts = ["morrisons_calendar.calendar_date_date desc"];
    } else {
      fields = [
        "depot_outbound.total_demand_qty",
        "depot_outbound.total_deamnd_fulfilled_qty",
        "morrisons_calendar.business_week_start_date",
      ];

      filters = {
        "depot_outbound.min": min,
        "morrisons_calendar.calendar_date_date": "5 weeks",
      };

      sorts = ["morrisons_calendar.business_week_start_date desc"];
    }
  }

  const dynamic_fields =
    // eslint-disable-next-line no-template-curly-in-string
    '[{"category":"table_calculation","label":"Units Sort","expression":"${depot_outbound.total_demand_qty} - ${depot_outbound.total_deamnd_fulfilled_qty} ","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"units_short","_type_hint":"number"},{"category":"table_calculation","label":"Percentage Achieved","expression":"${depot_outbound.total_deamnd_fulfilled_qty}/${depot_outbound.total_demand_qty} ","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"percentage_achieved","_type_hint":"number"}]';

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    dynamic_fields
  );
  return response;
};

/**
 * Get product details from Looker...
 * @param min Mandatory product min that has to be passed to this call.
 * @param country Pass this optional value when store specific details are needed.
 * @param region Pass this optional value when store specific details are needed.
 */
const getCorporateDepotOutbound = async (
  min: string,
  isDay: boolean,
  country?: string | undefined,
  region?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "depot_outbound";
  const limit = 14;

  let filters = {};
  let fields: string[] = [];
  let sorts: string[] = [];

  if (isDay) {
    fields = [
      "depot_outbound.total_demand_qty",
      "depot_outbound.total_deamnd_fulfilled_qty",
      "morrisons_calendar.calendar_date_date",
    ];

    filters = {
      "depot_outbound.min": min,
      "location.Country": escapeChar(country),
      "location.Region": escapeChar(region),
      "morrisons_calendar.calendar_date_date": "14 days ago for 14 days",
    };

    sorts = ["morrisons_calendar.calendar_date_date desc"];
  } else {
    fields = [
      "depot_outbound.total_demand_qty",
      "depot_outbound.total_deamnd_fulfilled_qty",
      "morrisons_calendar.business_week_start_date",
    ];

    filters = {
      "depot_outbound.min": min,
      "location.Country": escapeChar(country),
      "location.Region": escapeChar(region),
      "morrisons_calendar.calendar_date_date": "5 weeks",
    };

    sorts = ["morrisons_calendar.business_week_start_date desc"];
  }

  const dynamic_fields =
    // eslint-disable-next-line no-template-curly-in-string
    '[{"category":"table_calculation","label":"Units Sort","expression":"${depot_outbound.total_demand_qty} - ${depot_outbound.total_deamnd_fulfilled_qty} ","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"units_short","_type_hint":"number"},{"category":"table_calculation","label":"Percentage Achieved","expression":"${depot_outbound.total_deamnd_fulfilled_qty}/${depot_outbound.total_demand_qty} ","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"percentage_achieved","_type_hint":"number"}]';

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    dynamic_fields
  );
  return response;
};

const getSupplierInbound = async (
  min: string,
  isDay: boolean,
  store?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "supplier_inbound";
  const limit = 42;

  let filters = {};
  let fields: string[] = [];
  let sorts: string[] = [];

  if (store) {
    if (isDay) {
      fields = [
        "supplier_inbound.Supplier_purchases_units_ordered",
        "supplier_inbound.Supplier_purchases_value_ordered",
        "supplier_inbound.Supplier_purchases_units_delivered",
        "supplier_inbound.Supplier_purchases_value_delivered",
        "morrisons_calendar.calendar_date_date",
      ];

      filters = {
        "supplier_inbound.MIN": min,
        "location.store_description": store,
        "morrisons_calendar.calendar_date_date": "14 days ago for 43 days",
      };

      sorts = ["morrisons_calendar.calendar_date_date desc"];
    } else {
      fields = [
        "supplier_inbound.Supplier_purchases_units_ordered",
        "supplier_inbound.Supplier_purchases_value_ordered",
        "supplier_inbound.Supplier_purchases_units_delivered",
        "supplier_inbound.Supplier_purchases_value_delivered",
        "morrisons_calendar.business_week_start_date",
      ];

      filters = {
        "supplier_inbound.MIN": min,
        "location.store_description": store,
        "morrisons_calendar.calendar_date_date": "4 weeks ago for 9 weeks",
      };

      sorts = ["morrisons_calendar.business_week_start_date desc"];
    }
  } else {
    if (isDay) {
      fields = [
        "supplier_inbound.Supplier_purchases_units_ordered",
        "supplier_inbound.Supplier_purchases_value_ordered",
        "supplier_inbound.Supplier_purchases_units_delivered",
        "supplier_inbound.Supplier_purchases_value_delivered",
        "morrisons_calendar.calendar_date_date",
      ];

      filters = {
        "supplier_inbound.MIN": min,
        "supplier_inbound.schl_dlvry_dt_key_date": "14 days ago for 42 days",
      };

      sorts = ["morrisons_calendar.calendar_date_date desc"];
    } else {
      fields = [
        "supplier_inbound.Supplier_purchases_units_ordered",
        "supplier_inbound.Supplier_purchases_value_ordered",
        "supplier_inbound.Supplier_purchases_units_delivered",
        "supplier_inbound.Supplier_purchases_value_delivered",
        "morrisons_calendar.business_week_start_date",
      ];

      filters = {
        "supplier_inbound.MIN": min,
        "supplier_inbound.schl_dlvry_dt_key_date": "5 weeks ago for 9 weeks",
      };

      sorts = ["morrisons_calendar.business_week_start_date desc"];
    }
  }

  const dynamic_fields =
    // eslint-disable-next-line no-template-curly-in-string
    '[{"category":"table_calculation","label":"Units Sort","expression":"${supplier_inbound.Supplier_purchases_units_ordered} - ${supplier_inbound.Supplier_purchases_units_delivered}","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"units_short","_type_hint":"number"},{"category":"table_calculation","label":"Percentage Achieved","expression":"${supplier_inbound.Supplier_purchases_units_delivered}/${supplier_inbound.Supplier_purchases_units_ordered}","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"percentage_achieved","_type_hint":"number"}]';

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    dynamic_fields
  );
  return response;
};

const getCorporateSupplierInbound = async (
  min: string,
  isDay: boolean,
  country?: string | undefined,
  region?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "supplier_inbound";
  const limit = 42;

  let filters = {};
  let fields: string[] = [];
  let sorts: string[] = [];

  if (isDay) {
    fields = [
      "supplier_inbound.Supplier_purchases_units_ordered",
      "supplier_inbound.Supplier_purchases_value_ordered",
      "supplier_inbound.Supplier_purchases_units_delivered",
      "supplier_inbound.Supplier_purchases_value_delivered",
      "morrisons_calendar.calendar_date_date",
    ];

    filters = {
      "supplier_inbound.MIN": min,
      "location.Country": escapeChar(country),
      "location.Region": escapeChar(region),
      "morrisons_calendar.calendar_date_date": "14 days ago for 43 days",
    };

    sorts = ["morrisons_calendar.calendar_date_date desc"];
  } else {
    fields = [
      "supplier_inbound.Supplier_purchases_units_ordered",
      "supplier_inbound.Supplier_purchases_value_ordered",
      "supplier_inbound.Supplier_purchases_units_delivered",
      "supplier_inbound.Supplier_purchases_value_delivered",
      "morrisons_calendar.business_week_start_date",
    ];

    filters = {
      "supplier_inbound.MIN": min,
      "location.Country": escapeChar(country),
      "location.Region": escapeChar(region),
      "morrisons_calendar.calendar_date_date": "4 weeks ago for 9 weeks",
    };

    sorts = ["morrisons_calendar.business_week_start_date desc"];
  }

  const dynamic_fields =
    // eslint-disable-next-line no-template-curly-in-string
    '[{"category":"table_calculation","label":"Units Sort","expression":"${supplier_inbound.Supplier_purchases_units_ordered} - ${supplier_inbound.Supplier_purchases_units_delivered}","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"units_short","_type_hint":"number"},{"category":"table_calculation","label":"Percentage Achieved","expression":"${supplier_inbound.Supplier_purchases_units_delivered}/${supplier_inbound.Supplier_purchases_units_ordered}","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"percentage_achieved","_type_hint":"number"}]';

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    dynamic_fields
  );
  return response;
};

/**
 * Get product sales forecast details from Looker...
 * @param min Mandatory product min that has to be passed to this call.
 * @param store Pass this optional value when store specific details are needed.
 */
const getProductSalesForecast = async (
  min: string,
  isDay: boolean,
  store?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "sales_forecast";
  const limit = 22;

  let filters = {};
  let fields: string[] = [];
  let sorts: string[] = [];

  if (isDay) {
    fields = [
      "morrisons_calendar.calendar_date_date",
      "sales_forecast.prediction_units",
    ];

    if (store) {
      filters = {
        "sales_forecast.min": min,
        "location.store_description": store,
        "morrisons_calendar.calendar_date_date": "1 days from now for 3 weeks",
      };
    } else {
      filters = {
        "sales_forecast.min": min,
        "morrisons_calendar.calendar_date_date": "1 days from now for 3 weeks",
      };
    }

    sorts = ["morrisons_calendar.calendar_date_date"];
  } else {
    fields = [
      "morrisons_calendar.business_week_start_date",
      "sales_forecast.prediction_units",
    ];

    if (store) {
      filters = {
        "sales_forecast.min": min,
        "location.store_description": store,
        "morrisons_calendar.business_week_start_date":
          "1 weeks from now for 2 weeks",
      };
    } else {
      filters = {
        "sales_forecast.min": min,
        "morrisons_calendar.business_week_start_date":
          "1 weeks from now for 2 weeks",
      };
    }

    sorts = ["morrisons_calendar.business_week_start_date"];
  }

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts
  );
  return response;
};

/**
 * Get product sales forecast details from Looker...
 * @param min Mandatory product min that has to be passed to this call.
 * @param country Pass this optional value when store specific details are needed.
 * @param region Pass this optional value when store specific details are needed.
 */
const getCorporateProductSalesForecast = async (
  min: string,
  isDay: boolean,
  country?: string | undefined,
  region?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "sales_forecast";
  const limit = 22;

  let filters = {};
  let fields: string[] = [];
  let sorts: string[] = [];

  if (isDay) {
    fields = [
      "morrisons_calendar.calendar_date_date",
      "sales_forecast.prediction_units",
    ];

    filters = {
      "sales_forecast.min": min,
      "location.Country": escapeChar(country),
      "location.Region": escapeChar(region),
      "morrisons_calendar.calendar_date_date": "1 days from now for 3 weeks",
    };

    sorts = ["morrisons_calendar.calendar_date_date"];
  } else {
    fields = [
      "morrisons_calendar.business_week_start_date",
      "sales_forecast.prediction_units",
    ];

    filters = {
      "sales_forecast.min": min,
      "location.Country": escapeChar(country),
      "location.Region": escapeChar(region),
      "morrisons_calendar.business_week_start_date":
        "1 weeks from now for 2 weeks",
    };

    sorts = ["morrisons_calendar.business_week_start_date"];
  }

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts
  );
  return response;
};

const getWastageAndMarkdownByWeek = async (
  min: string,
  isDay: boolean,
  store?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const limit = 14;
  let filters = {};
  if (store) {
    if (isDay) {
      filters = {
        "waste_markdown.MIN": min,
        "location.store_description": store,
        "waste_markdown.clndr_dt": "14 days ago for 14 days",
      };
    } else {
      filters = {
        "waste_markdown.MIN": min,
        "location.store_description": store,
        "morrisons_calendar.business_week_start_date": "5 weeks",
      };
    }
  } else {
    if (isDay) {
      filters = {
        "waste_markdown.MIN": min,
        "waste_markdown.clndr_dt": "14 days ago for 14 days",
      };
    } else {
      filters = {
        "waste_markdown.MIN": min,
        "morrisons_calendar.business_week_start_date": "5 weeks",
      };
    }
  }

  const model = "my_product_profile";
  const explore = "waste_markdown";
  const fields = [
    isDay
      ? "waste_markdown.clndr_dt"
      : "morrisons_calendar.business_week_start_date",
    "waste_markdown.total_waste_value",
    "waste_markdown.total_waste_units",
    "waste_markdown.total_markdown_value",
    "waste_markdown.total_markdown_units",
    "waste_mrkdn_sales.total_cost_of_sales", // Used for percentage calculations...
    "waste_mrkdn_sales.total_turnover", // Used for percentage calculations...
  ];

  const sorts = [
    isDay
      ? "waste_markdown.clndr_dt desc"
      : "morrisons_calendar.business_week_start_date desc",
  ];

  const dynamic_fields =
    // eslint-disable-next-line no-template-curly-in-string
    '[{"category":"table_calculation","label":"Total Waste And Markdown Units","expression":"${waste_markdown.total_markdown_units} + ${waste_markdown.total_waste_units}","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"waste_and_markdown_units","_type_hint":"number"},{"category":"table_calculation","label":"Total Waste And Markdown Value","expression":"${waste_markdown.total_markdown_value} +${waste_markdown.total_waste_value}","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"waste_and_markdown","_type_hint":"number"},{"category":"table_calculation","label":"Waste And Markdown Pct By Takings","expression":"${waste_and_markdown}/${waste_mrkdn_sales.total_turnover}","value_format":null,"value_format_name":"percent_2","_kind_hint":"measure","table_calculation":"waste_and_markdown_pct_by_takings","_type_hint":"number"},{"category":"table_calculation","label":"Waste Pct By Takings","expression":"${waste_markdown.total_waste_value}/${waste_mrkdn_sales.total_turnover}","value_format":null,"value_format_name":"percent_2","_kind_hint":"measure","table_calculation":"waste_pct_by_takings","_type_hint":"number"},{"category":"table_calculation","label":"Markdown Pct By Takings","expression":"${waste_markdown.total_markdown_value}/${waste_mrkdn_sales.total_turnover}","value_format":null,"value_format_name":"percent_2","_kind_hint":"measure","table_calculation":"markdown_pct_by_takings","_type_hint":"number"},{"category":"table_calculation","label":"Waste And Markdown Pct By Cost","expression":"${waste_and_markdown}/${waste_mrkdn_sales.total_cost_of_sales}","value_format":null,"value_format_name":"percent_2","_kind_hint":"measure","table_calculation":"waste_and_markdown_pct_by_cost","_type_hint":"number"},{"category":"table_calculation","label":"Waste Pct By Cost","expression":"${waste_markdown.total_waste_value}/${waste_mrkdn_sales.total_cost_of_sales}","value_format":null,"value_format_name":"percent_2","_kind_hint":"measure","table_calculation":"waste_pct_by_cost","_type_hint":"number"},{"category":"table_calculation","label":"Markdown Pct By Cost","expression":"${waste_markdown.total_markdown_value}/${waste_mrkdn_sales.total_cost_of_sales}","value_format":null,"value_format_name":"percent_2","_kind_hint":"measure","table_calculation":"markdown_pct_by_cost","_type_hint":"number"}]';

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    dynamic_fields
  );
  return response;
};

const getCorporateWastageAndMarkdownByWeek = async (
  min: string,
  isDay: boolean,
  country?: string | undefined,
  region?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const limit = 14;
  let filters = {};

  if (isDay) {
    filters = {
      "waste_markdown.MIN": min,
      "location.Country": escapeChar(country),
      "location.Region": escapeChar(region),
      "waste_markdown.clndr_dt": "14 days ago for 14 days",
    };
  } else {
    filters = {
      "waste_markdown.MIN": min,
      "location.Country": escapeChar(country),
      "location.Region": escapeChar(region),
      "morrisons_calendar.business_week_start_date": "5 weeks",
    };
  }

  const model = "my_product_profile";
  const explore = "waste_markdown";
  const fields = [
    isDay
      ? "waste_markdown.clndr_dt"
      : "morrisons_calendar.business_week_start_date",
    "waste_markdown.total_waste_value",
    "waste_markdown.total_waste_units",
    "waste_markdown.total_markdown_value",
    "waste_markdown.total_markdown_units",
    "waste_mrkdn_sales.total_cost_of_sales", // Used for percentage calculations...
    "waste_mrkdn_sales.total_turnover", // Used for percentage calculations...
  ];

  const sorts = [
    isDay
      ? "waste_markdown.clndr_dt desc"
      : "morrisons_calendar.business_week_start_date desc",
  ];

  const dynamic_fields =
    // eslint-disable-next-line no-template-curly-in-string
    '[{"category":"table_calculation","label":"Total Waste And Markdown Units","expression":"${waste_markdown.total_markdown_units} + ${waste_markdown.total_waste_units}","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"waste_and_markdown_units","_type_hint":"number"},{"category":"table_calculation","label":"Total Waste And Markdown Value","expression":"${waste_markdown.total_markdown_value} +${waste_markdown.total_waste_value}","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"waste_and_markdown","_type_hint":"number"},{"category":"table_calculation","label":"Waste And Markdown Pct By Takings","expression":"${waste_and_markdown}/${waste_mrkdn_sales.total_turnover}","value_format":null,"value_format_name":"percent_2","_kind_hint":"measure","table_calculation":"waste_and_markdown_pct_by_takings","_type_hint":"number"},{"category":"table_calculation","label":"Waste Pct By Takings","expression":"${waste_markdown.total_waste_value}/${waste_mrkdn_sales.total_turnover}","value_format":null,"value_format_name":"percent_2","_kind_hint":"measure","table_calculation":"waste_pct_by_takings","_type_hint":"number"},{"category":"table_calculation","label":"Markdown Pct By Takings","expression":"${waste_markdown.total_markdown_value}/${waste_mrkdn_sales.total_turnover}","value_format":null,"value_format_name":"percent_2","_kind_hint":"measure","table_calculation":"markdown_pct_by_takings","_type_hint":"number"},{"category":"table_calculation","label":"Waste And Markdown Pct By Cost","expression":"${waste_and_markdown}/${waste_mrkdn_sales.total_cost_of_sales}","value_format":null,"value_format_name":"percent_2","_kind_hint":"measure","table_calculation":"waste_and_markdown_pct_by_cost","_type_hint":"number"},{"category":"table_calculation","label":"Waste Pct By Cost","expression":"${waste_markdown.total_waste_value}/${waste_mrkdn_sales.total_cost_of_sales}","value_format":null,"value_format_name":"percent_2","_kind_hint":"measure","table_calculation":"waste_pct_by_cost","_type_hint":"number"},{"category":"table_calculation","label":"Markdown Pct By Cost","expression":"${waste_markdown.total_markdown_value}/${waste_mrkdn_sales.total_cost_of_sales}","value_format":null,"value_format_name":"percent_2","_kind_hint":"measure","table_calculation":"markdown_pct_by_cost","_type_hint":"number"}]';

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    dynamic_fields
  );
  return response;
};

const getStoreStock = async (
  min: string,
  store?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const limit = 14;
  let filters = {};
  if (store) {
    filters = {
      "store_depot_stock.MIN": min,
      "store_depot_stock.trx_date": "1 days ago for 1 days",
      "location.store_description": store,
    };
  } else {
    filters = {
      "store_depot_stock.MIN": min,
      "store_depot_stock.trx_date": "1 days ago for 1 days",
    };
  }

  const model = "my_product_profile";
  const explore = "store_depot_stock";
  const fields = [
    "store_depot_stock.total_store_stock_volume",
    "store_depot_stock.total_transit_stock_volume",
    "store_depot_stock.total_depotstockvolume",
  ];

  const dynamic_fields =
    // eslint-disable-next-line no-template-curly-in-string
    '[{"category":"table_calculation","label":"Total Stock","expression":"${store_depot_stock.total_store_stock_volume} + ${store_depot_stock.total_transit_stock_volume} + ${store_depot_stock.total_depotstockvolume}","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"total_stock","_type_hint":"number"}]';

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    undefined,
    undefined,
    dynamic_fields
  );
  return response;
};

const getCorporateStoreStock = async (
  min: string,
  country?: string | undefined,
  region?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const limit = 14;
  let filters = {};
  filters = {
    "store_depot_stock.MIN": min,
    "store_depot_stock.trx_date": "1 days ago for 1 days",
    "location.Country": escapeChar(country),
    "location.Region": escapeChar(region),
  };

  const model = "my_product_profile";
  const explore = "store_depot_stock";
  const fields = [
    "store_depot_stock.total_store_stock_volume",
    "store_depot_stock.total_transit_stock_volume",
    "store_depot_stock.total_depotstockvolume",
  ];

  const dynamic_fields =
    // eslint-disable-next-line no-template-curly-in-string
    '[{"category":"table_calculation","label":"Total Stock","expression":"${store_depot_stock.total_store_stock_volume} + ${store_depot_stock.total_transit_stock_volume} + ${store_depot_stock.total_depotstockvolume}","value_format":null,"value_format_name":"decimal_2","_kind_hint":"measure","table_calculation":"total_stock","_type_hint":"number"}]';

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    undefined,
    undefined,
    dynamic_fields
  );
  return response;
};

const getStoreStockHistory = async (
  min: string,
  store?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "store_depot_stock";
  let fields = [];
  let sorts = [];
  let filters = {};
  const limit = 14;

  fields = [
    "store_depot_stock.trx_date",
    "store_depot_stock.total_store_stock_volume",
    "store_depot_stock.total_store_stock_value",
    "store_depot_stock.total_transit_stock_volume",
    "store_depot_stock.total_transit_stock_value",
    "store_depot_stock.total_depotstockvolume",
    "store_depot_stock.total_depotstockvalue",
  ];

  if (store) {
    filters = {
      "store_depot_stock.MIN": min,
      "location.store_description": store,
      "store_depot_stock.trx_date": "14 days ago for 14 days",
    };
  } else {
    filters = {
      "store_depot_stock.MIN": min,
      "store_depot_stock.trx_date": "14 days ago for 14 days",
    };
  }

  sorts = ["morrisons_calendar.calendar_date_date DESC"];

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    undefined
  );
  return response;
};

const getCorporateStoreStockHistory = async (
  min: string,
  country?: string | undefined,
  region?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "store_depot_stock";
  let fields = [];
  let sorts = [];
  let filters = {};
  const limit = 14;

  fields = [
    "store_depot_stock.trx_date",
    "store_depot_stock.total_store_stock_volume",
    "store_depot_stock.total_store_stock_value",
    "store_depot_stock.total_transit_stock_volume",
    "store_depot_stock.total_transit_stock_value",
    "store_depot_stock.total_depotstockvolume",
    "store_depot_stock.total_depotstockvalue",
  ];

  filters = {
    "store_depot_stock.MIN": min,
    "location.Country": escapeChar(country),
    "location.Region": escapeChar(region),
    "store_depot_stock.trx_date": "14 days ago for 14 days",
  };

  sorts = ["morrisons_calendar.calendar_date_date DESC"];

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    undefined
  );
  return response;
};

const getCompetitorPrice = async (
  min: string
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "assosia_cmptr_price";
  const fields = [
    "assosia_cmptr_price_with_min.competitor_name",
    "assosia_cmptr_price_with_min.base_price",
    "assosia_cmptr_price_with_min.offer_type",
    "assosia_cmptr_price_with_min.offer_price",
    "assosia_cmptr_price_with_min.mix_and_match_quantity",
    "assosia_cmptr_price_with_min.product_description",
    "assosia_cmptr_price_with_min.file_date",
  ];
  const sorts = ["assosia_cmptr_price_with_min.competitor_name ASC"];
  const limit = 14;
  const filters = {
    "assosia_cmptr_price_with_min.MIN": min,
  };

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    //dynamic_fieldsset PATH=%PATH%;C:\Users\systcsyv\AppData\Roaming\npm
    undefined
  );
  return response;
};

const getRetroDeals = async (
  min: string
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "retro_deal";
  const fields = [
    "retro_deal.bsns_wk_strt_dt",
    "retro_deal.deal_description",
    "retro_deal.collect_start_date",
    "retro_deal.collect_end_date",
    "retro_deal.weekly_deal_income_amt",
    "retro_deal.per_unit_income",
  ];
  const sorts = ["retro_deal.bsns_wk_strt_dt DESC"];
  const limit = 14;

  const filters = {
    "retro_deal.MIN": min,
    "retro_deal.bsns_wk_strt_dt": "5 weeks",
  };

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    //dynamic_fields
    undefined
  );
  return response;
};

const getRetroDealsHistory = async (
  min: string
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "retro_deal_history";
  const fields = [
    "retro_deal_history.bsns_wk_strt_dt",
    "retro_deal_history.deal_description",
    "retro_deal_history.collect_start_date",
    "retro_deal_history.collect_end_date",
    "retro_deal_history.weekly_deal_income_amt",
    "retro_deal_history.per_unit_income",
  ];
  const sorts = ["retro_deal_history.bsns_wk_strt_dt DESC"];
  const limit = 500;

  const filters = {
    "retro_deal_history.MIN": min,
  };

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    undefined
  );
  return response;
};

const getWeekNumbers = async (): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "calendarisland";
  const fields = [
    "calendarisland.RPRTNG_CMPNY_WK",
    "calendarisland.business_week_start_date",
  ];
  const sorts = undefined;
  const limit = 200;
  const filters = {
    "calendarisland.business_date_date":
      "18 months ago for 18 months,after 1 months ago",
  };

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    //dynamic_fields
    undefined
  );
  return response;
};

const getDelistingDetails = async (
  min: string,
  store?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "delist_derange";
  const fields = [
    "delist_derange.due_to_be_delisted",
    "delist_derange.delist_date",
    "delist_derange.due_to_be_deranged",
    "delist_derange.derange_date",
  ];
  const sorts = undefined;
  const limit = 1;

  let filters = {};
  if (store) {
    filters = {
      "delist_derange.MIN": min,
      "location.store_description": store,
    };
  } else {
    filters = {
      "delist_derange.MIN": min,
    };
  }

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    //dynamic_fields
    undefined
  );
  return response;
};

const getCorporateDelistingDetails = async (
  min: string,
  country?: string,
  region?: string
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "delist_derange";
  const fields = [
    "delist_derange.Store_count_due_to_be_deranged",
    "delist_derange.Store_count_due_to_be_delisted",
  ];
  const filters = {
    "delist_derange.MIN": min,
    "location.Country": escapeChar(country),
    "location.Region": escapeChar(region),
  };
  const sorts = undefined;
  const limit = 1;

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    //dynamic_fields
    undefined
  );
  return response;
};

const getCorporateStoresPlanned = async (
  min: string,
  country?: string,
  region?: string,
  planogramClass?: string
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "location_space_info_plano";
  const fields = ["location.store_description"];
  const filters = {
    "location_space_info_plano.MIN": min,
    "location.Country": escapeChar(country),
    "location.Region": escapeChar(region),
    "location_space_info_plano.Planogram_Class": escapeChar(planogramClass),
  };
  const sorts = ["location.store_description ASC"];
  const limit = 700; // Maximum 700 stores are possible in Morrisons from 001 to 699...

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    //dynamic_fields
    undefined
  );
  return response;
};

const getCorporateStoresToBeDelisted = async (
  min: string,
  country?: string,
  region?: string
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "delist_derange";
  const fields = ["location.store_description", "delist_derange.delist_date"];
  const filters = {
    "delist_derange.MIN": min,
    "location.Country": escapeChar(country),
    "location.Region": escapeChar(region),
    "delist_derange.due_to_be_delisted": "Yes",
  };
  const sorts = ["location.store_description ASC"];
  const limit = 700; // Maximum 700 stores are possible in Morrisons from 001 to 699...

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    //dynamic_fields
    undefined
  );
  return response;
};

const getCorporateStoresToBeDeranged = async (
  min: string,
  country?: string,
  region?: string
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "delist_derange";
  const fields = ["location.store_description", "delist_derange.delist_date"];
  const filters = {
    "delist_derange.MIN": min,
    "location.Country": escapeChar(country),
    "location.Region": escapeChar(region),
    "delist_derange.due_to_be_deranged": "Yes",
  };
  const sorts = ["location.store_description ASC"];
  const limit = 700; // Maximum 700 stores are possible in Morrisons from 001 to 699...

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    //dynamic_fields
    undefined
  );
  return response;
};

const getLastDeliveryDate = async (
  min: string,
  store?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "supplier_inbound";
  const limit = 1;

  let filters = {};
  let fields: string[] = [];

  if (store) {
    fields = ["supplier_inbound.Last_Delivery_date"];

    filters = {
      "supplier_inbound.MIN": min,
      "location.store_description": store,
    };
  } else {
    fields = ["supplier_inbound.Last_Delivery_date"];

    filters = {
      "supplier_inbound.MIN": min,
    };
  }

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    undefined,
    undefined,
    undefined
  );

  return response;
};

const getCorporateLastDeliveryDate = async (
  min: string,
  country?: string | undefined,
  region?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "supplier_inbound";
  const limit = 1;

  let filters = {};
  let fields: string[] = [];

  fields = ["supplier_inbound.Last_Delivery_date"];

  filters = {
    "supplier_inbound.MIN": min,
    "location.Country": escapeChar(country),
    "location.Region": escapeChar(region),
  };

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    undefined,
    undefined,
    undefined
  );

  return response;
};

const getMinOrderQuantity = async (
  min: string,
  store?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "min_order_qty";
  const limit = 1;

  let filters = {};
  let fields: string[] = [];

  if (store) {
    fields = [
      "min_order_qty.moq_type",
      "min_order_qty.PAD_Flag",
      "min_order_qty.min_order_qty_cases",
      "min_order_qty.Do_not_order_flag",
      "min_order_qty.serving_depot",
    ];

    filters = {
      "min_order_qty.MIN": min,
      "location.store_description": store,
    };
  } else {
    fields = [
      "min_order_qty.moq_type",
      "min_order_qty.PAD_Flag",
      "min_order_qty.min_order_qty_cases",
      "min_order_qty.Do_not_order_flag",
    ];

    filters = {
      "min_order_qty.MIN": min,
    };
  }

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    undefined,
    undefined,
    undefined
  );

  return response;
};

const getCorporateMinOrderQuantity = async (
  min: string,
  country?: string | undefined,
  region?: string | undefined
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "min_order_qty";
  const limit = 1;

  let filters = {};
  let fields: string[] = [];

  fields = [
    "min_order_qty.moq_type",
    "min_order_qty.PAD_Flag",
    "min_order_qty.min_order_qty_cases",
    "min_order_qty.Do_not_order_flag",
    "min_order_qty.serving_depot",
  ];

  filters = {
    "min_order_qty.MIN": min,
    "location.Country": escapeChar(country),
    "location.Region": escapeChar(region),
  };

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    undefined,
    undefined,
    undefined
  );

  return response;
};

const getSupplierCost = async (
  min: string
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "unit_cost";
  const limit = 500;

  const fields = [
    "unit_cost.PIN",
    "unit_cost.Supplier_ID",
    "unit_cost.Supplier_Name",
    "unit_cost.Supplier_Site_ID",
    "unit_cost.Supplier_Site_Name",
    "unit_cost.Pack_Size",
    "unit_cost.Supplier_Unit_Cost",
    "unit_cost.Primary_Supplier_Indicator",
    "unit_cost.TI",
    "unit_cost.HI",
    "unit_cost.Pallet_Size",
    "unit_cost.Case_Cost",
    "unit_cost.Supplier_Currency",
    "unit_cost.Country_Of_Supply"
  ];

  const filters = {
    "unit_cost.MIN": min,
  };

  const sorts = [
    "unit_cost.Primary_Supplier_Indicator DESC",
    "unit_cost.PIN ASC",
    "unit_cost.Supplier_ID ASC",
    "unit_cost.Supplier_Site_ID ASC",
  ];

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts,
    undefined,
    undefined
  );
  return response;
};

const getProductMergin = async (
  min: string
): Promise<{ ok: boolean; value: any[]; error: any }> => {
  // Input parameters...
  const model = "my_product_profile";
  const explore = "product";
  const limit = 1;

  const fields = [
    "current_margin.Current_Margin",
    "current_margin.Current_Margin_Pct",
  ];

  const filters = {
    "product.min": min,
  };

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    undefined,
    undefined,
    undefined
  );
  return response;
};

const getTotalRanged = async (
  min: string | undefined
): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  const limit = 1;
  const filters: {
    "product_search.min"?: string;
  } = {};

  if (min && min.length > 0) {
    filters["product_search.min"] = min;
  }

  const model = "my_product_profile";
  const explore = "product_search";
  const fields = ["product_search.stores_ranged"];
  const sorts = undefined;

  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    limit,
    sorts
  );
  return response;
};

const getStoreReangedStatus = async (
  min: string,
  store: string | undefined
): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  const filters = {
    "store_stock.MIN": min,
    "location.store_description": store,
  };
  const model = "my_product_profile";
  const explore = "store_stock";
  const fields = ["store_stock.store_range_ind"];
  const response = await executeAPICall(model, explore, fields, filters, 1);
  return response;
};

const getCorporateStoreReangedStatus = async (
  min: string,
  country?: string,
  region?: string
): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  const model = "my_product_profile";
  const explore = "store_stock";
  const fields = ["store_stock.Store_Range", "store_stock.Depot_Range"];
  const filters = {
    "store_stock.MIN": min,
    "location.Country": escapeChar(country),
    "location.Region": escapeChar(region),
  };
  const response = await executeAPICall(model, explore, fields, filters, 1);
  return response;
};

const getRangeInformation = async (
  min: string,
  store: string | undefined
): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  const filters = {
    "store_stock.MIN": min,
    "location.store_description": store,
  };
  const model = "my_product_profile";
  const explore = "store_stock";
  const fields = [
    "store_stock.promo_space",
    "store_stock.standard_space",
    "store_stock.discret_space",
    "store_stock.Total_Space",
  ];
  const response = await executeAPICall(model, explore, fields, filters, 2);
  return response;
};

const getPreviousRangeInformation = async (
  min: string,
  store: string | undefined
): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  const filters = {
    "store_stock_range.MIN": min,
    "location.store_description": store,
  };
  const model = "my_product_profile";
  const explore = "store_stock_range";
  const fields = [
    "store_stock_range.discret_space",
    "store_stock_range.promo_space",
    "store_stock_range.standard_space",
    "store_stock_range.Total_Space",
  ];
  const response = await executeAPICall(model, explore, fields, filters, 2);
  return response;
};

const getCorporateRangeInformation = async (
  min: string,
  country?: string,
  region?: string
): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  const filters = {
    "store_stock.MIN": min,
    "location.Country": escapeChar(country),
    "location.Region": escapeChar(region),
  };
  const model = "my_product_profile";
  const explore = "store_stock";
  const fields = [
    "store_stock.promo_space",
    "store_stock.standard_space",
    "store_stock.discret_space",
    "store_stock.Total_Space",
  ];
  const response = await executeAPICall(model, explore, fields, filters, 2);
  return response;
};

const getCorporatePreviousRangeInformation = async (
  min: string,
  country?: string,
  region?: string
): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  const filters = {
    "store_stock_range.MIN": min,
    "location.Country": escapeChar(country),
    "location.Region": escapeChar(region),
  };

  const model = "my_product_profile";
  const explore = "store_stock_range";
  const fields = [
    "store_stock_range.discret_space",
    "store_stock_range.promo_space",
    "store_stock_range.standard_space",
    "store_stock_range.Total_Space",
  ];
  const response = await executeAPICall(model, explore, fields, filters, 2);
  return response;
};

const getSpaceInformation = async (
  min: string,
  store: string | undefined
): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  const filters = {
    "location_space_info_plano.MIN": min,
    "location.store_description": store,
  };
  const model = "my_product_profile";
  const explore = "location_space_info_plano";
  const fields = [
    "location_space_info_plano.aisle",
    "location_space_info_plano.facings_deep",
    "location_space_info_plano.facings_high",
    "location_space_info_plano.facings_wide",
    "location_space_info_plano.Planogram_Class",
    "location_space_info_plano.position_from_left",
    "location_space_info_plano.shelf_from_bottom",
    "location_space_info_plano.starts_bay",
    "location_space_info_plano.units",
  ];
  const response = await executeAPICall(model, explore, fields, filters, 50);
  return response;
};

const getCorporateSpaceInformation = async (
  min: string,
  country?: string,
  region?: string
): Promise<{
  ok: boolean;
  value: any[];
  error: any;
}> => {
  const filters = {
    "location_space_info_plano.MIN": min,
    "location.Country": escapeChar(country),
    "location.Region": escapeChar(region),
  };

  const model = "my_product_profile";
  const explore = "location_space_info_plano";
  const fields = [
    "location_space_info_plano.Planogram_Class",
    "location_space_info_plano.Fill_quantity",
    "location_space_info_plano.number_of_stores",
  ];
  const sorts = ["location_space_info_plano.Planogram_Class"];
  const response = await executeAPICall(
    model,
    explore,
    fields,
    filters,
    50,
    sorts
  );
  return response;
};

const executeAPICall = async (
  model: string,
  explore: string,
  fields: string[] | null | undefined,
  filters: any,
  limit?: number | 5000,
  sorts?: string[] | undefined,
  filter_expression?: string | undefined,
  dynamic_fields?: string,
  format?: string // For future use to download data as CSV...
): Promise<any> => {
  const body = {
    model: model,
    view: explore,
    fields: fields,
    filters: filters,
    sorts: sorts,
    filter_expression: filter_expression,
    dynamic_fields: dynamic_fields,
  };

  if (Util.getDebugMode()) console.log(JSON.stringify(body));

  const promise = sdk.run_inline_query({
    result_format: format ? format : "json",
    limit: limit, // Any query that returns more than 5000 rows is not an ideal candidate for online reporting.
    body: body,
  });

  return promise;
};

const logout = () => {
  sdk.logout();
};

export {
  getProductDetails,
  logout,
  getCommercialHierarchy,
  getCommercialBands,
  getSupplierName,
  searchProduct,
  getProductSales,
  getProductSalesByChannel,
  getProductSalesForecast,
  getWastageAndMarkdownByWeek,
  getDepotOutbound,
  getSupplierInbound,
  getStoreStock,
  getStoreStockHistory,
  getCompetitorPrice,
  getRetroDeals,
  getRetroDealsHistory,
  getWeekNumbers,
  getStoreDescriptions,
  getDelistingDetails,
  getLastDeliveryDate,
  getMinOrderQuantity,
  getSupplierCost,
  getProductMergin,
  getTotalRanged,
  getStoreReangedStatus,
  getSpaceInformation,
  getRangeInformation,
  getPreviousRangeInformation,
  getCountriesAndRegions,
  getCorporateSpaceInformation,
  getCorporatePreviousRangeInformation,
  getCorporateRangeInformation,
  getCorporateDelistingDetails,
  getCorporateStoreReangedStatus,
  getCorporateProductSales,
  getCorporateProductSalesForecast,
  getCorporateWastageAndMarkdownByWeek,
  getCorporateStoreStock,
  getCorporateStoreStockHistory,
  getCorporateDepotOutbound,
  getCorporateSupplierInbound,
  getCorporateLastDeliveryDate,
  getCorporateMinOrderQuantity,
  getCorporateStoresToBeDelisted,
  getCorporateStoresToBeDeranged,
  getCorporateStoresPlanned,
};
